'use client';

import { ENGLISH_IQ_STORED_SESSION_VERSION, type SupportedLocaleType } from '@repo/config';
import type { IEnglishIqResult, IEnglishIqStoredSession } from '@repo/sdk';
import { useCallback, useEffect, useState } from 'react';
import {
  type IEnglishIqStoredFlow,
  persistEnglishIqSession,
  readEnglishIqSession,
} from '../../state';

function toActiveFlow(session: IEnglishIqStoredSession): IEnglishIqStoredFlow {
  if (session.milestone) {
    return { phase: 'milestone', session: { ...session, milestone: session.milestone } };
  }
  return { phase: 'question', session: { ...session, milestone: null } };
}

export function useEnglishIqStoredFlow(locale: SupportedLocaleType) {
  const [flow, setFlow] = useState<IEnglishIqStoredFlow | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const storeFlow = useCallback((next: IEnglishIqStoredFlow | null): void => {
    setFlow(next);
    persistEnglishIqSession(next);
  }, []);
  const clearSession = useCallback((): void => storeFlow(null), [storeFlow]);
  const storeSession = useCallback(
    (session: IEnglishIqStoredSession): void => storeFlow(toActiveFlow(session)),
    [storeFlow],
  );
  const storeCompleting = useCallback(
    (token: string): void => {
      if (flow?.phase !== 'question' && flow?.phase !== 'milestone') {
        return;
      }
      storeFlow({
        expiresAt: flow.session.expiresAt,
        locale: flow.session.locale,
        phase: 'completing',
        startedAt: flow.session.startedAt,
        token,
        version: ENGLISH_IQ_STORED_SESSION_VERSION,
      });
    },
    [flow, storeFlow],
  );
  const storeResult = useCallback(
    (result: IEnglishIqResult): void =>
      storeFlow({ locale, phase: 'result', result, version: ENGLISH_IQ_STORED_SESSION_VERSION }),
    [locale, storeFlow],
  );
  const clearMilestone = useCallback((): void => {
    if (flow?.phase !== 'milestone') {
      return;
    }
    storeSession({ ...flow.session, milestone: null });
  }, [flow, storeSession]);

  useEffect(() => {
    const restored = readEnglishIqSession(locale);
    setFlow(restored);
    if (!restored) {
      persistEnglishIqSession(null);
    }
    setIsHydrated(true);
  }, [locale]);

  const isActive = flow?.phase === 'question' || flow?.phase === 'milestone';
  return {
    clearMilestone,
    clearSession,
    completingToken: flow?.phase === 'completing' ? flow.token : null,
    isHydrated,
    milestone: flow?.phase === 'milestone' ? flow.session.milestone : null,
    result: flow?.phase === 'result' ? flow.result : null,
    session: isActive ? flow.session : null,
    storeCompleting,
    storeResult,
    storeSession,
  };
}
